<template>
  <div>
    <async-loader v-if="computedLoadingSubGroupDetails" />
    <div v-else>
      <div container>
        <h1>{{ computedSubGroup.subGroupName }}</h1>
        <h2>Subgroup Details</h2>
        <div grid>
          <div column>
            <labeled-data
              label="Subgroup ID:"
              :data="computedSubGroup.subGroupId"
            />
          </div>
          <div column>
            <labeled-data
              label="Effective Date:"
              :data="formatDate(computedSubGroup.effectiveDate)"
            />
          </div>
          <div column>
            <labeled-data
              label="Term Date:"
              :data="formatDate(computedSubGroup.terminationDate)"
            />
          </div>
        </div>
        <div grid>
          <div column>
            <labeled-data
              label="Account Manager:"
              :data="computedSubGroup.accountManagerName"
            />
          </div>
          <div column>
            <labeled-data
              label="Account Manager Phone:"
              :data="computedSubGroup.accountManagerPhone"
            />
          </div>
          <div column>
            <labeled-data
              label="Account Manager Email:"
              :data="computedSubGroup.accountManagerEmail"
            />
          </div>
        </div>
      </div>

      <!--<async-loader v-if='computedLoadingBenefits'/>-->
      <!--<benefit-summary v-else-if="computedBenefits && computedBenefits.length > 0" v-for="(benefit, index) in computedBenefits" :benefit="benefit" :key="index" />-->

      <collapsible-section class="_pt0 _pb0 _bb1">
        <template #headerText>
          <p class="color-leaf">Class / Plan Listing</p>
        </template>
        <template #collapsibleSection>
          <div container>
            <form @submit.prevent="search" class="against-grey-bg">
              <div grid>
                <div column>
                  <div class="form-group">
                    <date-input
                      v-model.trim="effectiveDate"
                      valueKey="effectiveDate"
                      label="Effective Date"
                    />

                    <a v-if="effectiveDate" @click="effectiveDate = ''"
                      >clear</a
                    >
                  </div>
                </div>
                <div column>
                  <div class="form-group">
                    <date-input
                      v-model.trim="termDate"
                      valueKey="termDate"
                      label="Term Date:"
                    />
                    <a v-if="termDate" @click="termDate = ''">clear</a>
                  </div>
                </div>
              </div>
            </form>
            <advanced-table
              :headers="classPlanHeaders"
              :items="computedClassPlanForTable"
              :rowsPerPage="10"
              class="_p0"
            >
              <template #items="{ item, visibleHeaders, getCellValue }">
                <tr>
                  <template v-for="(header, index) in visibleHeaders">
                    <td v-if="header.value === 'benefits'" :key="index">
                      <a href="javascript:void(0)" @click="viewBenefits(item)">
                        View Benefits
                      </a>
                    </td>
                    <td v-else :key="index">
                      {{ getCellValue(item, header) }}
                    </td>
                  </template>
                </tr>
              </template>
            </advanced-table>
          </div>
        </template>
      </collapsible-section>
    </div>
  </div>
</template>

<script>
import DateInput from '@atoms/DateInput';
import LabeledData from '@molecules/LabeledData';
import AsyncLoader from '@molecules/AsyncLoader';
import CollapsibleSection from '@sections/CollapsibleSection';
import AdvancedTable from '@organisms/AdvancedTableReplace.vue';

import GroupApi from '@api/GroupApi';

import ClassPlanFromSubGroupCkData from '@mixins/ClassPlanFromSubGroupCkData';
import formattable from '@mixins/formattable';

import moment from 'moment';

export default {
  mixins: [ClassPlanFromSubGroupCkData, formattable],
  components: {
    CollapsibleSection,
    AdvancedTable,
    DateInput,
    LabeledData,
    AsyncLoader
  },
  data() {
    return {
      contextDependent: false,
      benefits: {},
      // subGroupCk: this.subGroupCkFromRouteParams,
      effectiveDate: '',
      termDate: '',
      subGroupDetails: {},
      classPlanHeaders: [
        {
          text: 'Class',
          align: 'left',
          value: 'class',
          truncate: true,
          maxChars: 42
        },
        { text: 'Plan', value: 'plan', truncate: true, maxChars: 32 },
        { text: 'Effective Date', value: 'effectiveDate' },
        { text: 'Term Date', value: 'termDate' },
        { text: 'Benefits', value: 'benefits' }
      ]
    };
  },
  computed: {
    groupCkFromRouteParams() {
      return this.$store.getters['groupSelector/groupDetailCk'];
    },
    subGroupCkFromRouteParams() {
      return this.$store.getters['groupSelector/subGroupDetailCk'];
    },
    computedLoadingSubGroupDetails() {
      return (
        Object.keys(this.classPlanSubGroups).length === 0 ||
        Object.keys(this.subGroupDetails).length === 0
      );
    },
    computedLoadingBenefits() {
      return Object.keys(this.benefits).length === 0;
    },
    computedBenefits() {
      return this.benefits;
    },
    computedSubGroup() {
      if (Object.keys(this.subGroupDetails).length === 0) {
        return {};
      }

      if (this.subGroupDetails.groups) {
        for (let i = 0; i < this.subGroupDetails.groups.length; ++i) {
          const group = this.subGroupDetails.groups[i];

          const subGroup = group.subGroups.find(
            sg => this.subGroupCkFromRouteParams
          );
          if (subGroup) {
            return this.combineGroupAndSubGroupData(group, subGroup);
          }
        }
      }

      return {};
    },
    computedClassPlanForTable() {
      let classPlans = this.computedClassPlans.map(cp => ({
        class: `${cp.classDescription} - ${cp.classId}`,
        plan: `${cp.planDescription} - ${cp.planId}`,
        effectiveDate: this.formatDate(cp.planEffectiveDate),
        termDate: this.formatDate(cp.planTerminationDate),
        ...cp
      }));

      if (this.effectiveDate !== '') {
        classPlans = classPlans.filter(cp =>
          moment(cp.effectiveDate).isSameOrAfter(this.effectiveDate)
        );
      }

      if (this.termDate !== '') {
        classPlans = classPlans.filter(cp =>
          moment(cp.termDate).isSameOrBefore(this.termDate)
        );
      }

      return classPlans;
    }
  },
  methods: {
    async fetchSubgroupData(groupCk, subGroupCk) {
      console.log('FETCH', groupCk, subGroupCk);

      new GroupApi({ simulateExample: 'Sub Group Detail' })
        .subGroup({
          groupCk,
          subGroupCk
        })
        .then(res => {
          this.subGroupDetails = res;
        })
        .catch(err => {
          // TODO handle error
          console.log('err', err);
        });
      this.requestClassPlansForSubGroup(this.subGroupCkFromRouteParams);
    },
    viewBenefits(item) {
      this.$router.push({
        name: 'group-benefits',
        params: {
          groupCk: this.computedSubGroup.groupCk,
          groupName: this.computedSubGroup.groupName,
          groupId: this.computedSubGroup.groupId,
          subGroupId: this.computedSubGroup.subGroupId,
          subGroupName: this.computedSubGroup.subGroupName,
          classId: item.classId,
          planId: item.planId,
          productId: item.product,
          token: this.classPlanSubGroups[0].token
        },
        query: {
          serviceDate: this.formatDate(item.benefitServiceDate, 'YYYY-MM-DD')
        }
      });
    },
    combineGroupAndSubGroupData(group, subGroup) {
      return {
        ...{
          accountManagerName: group.accountManagerName,
          accountManagerPhone: group.accountManagerPhone,
          accountManagerEmail: group.accountManagerEmail,
          groupId: group.groupId,
          groupCk: group.groupCk,
          groupName: group.groupName
        },
        ...subGroup
      };
    }
  },
  watch: {
    groupCkFromRouteParams() {
      console.log('groupCK watch');

      this.fetchSubgroupData(
        this.groupCkFromRouteParams,
        this.subGroupCkFromRouteParams
      );
    },
    subGroupCkFromRouteParams() {
      console.log('subgroupCK watch');
      this.fetchSubgroupData(
        this.groupCkFromRouteParams,
        this.subGroupCkFromRouteParams
      );
    }
    // $route(newRoute, oldRoute) {
    //   console.log('ROUTE WATCH');

    //   this.fetchSubgroupData(
    //     this.$route.params.groupCk,
    //     this.$route.params.subGroupCk
    //   );
    //   // new GroupApi({ simulateExample: 'Sub Group Detail' })
    //   //   .subGroup({
    //   //     groupCk: this.$route.params.groupCk,
    //   //     subGroupCk: this.$route.params.subGroupCk
    //   //   })
    //   //   .then(res => {
    //   //     this.subGroupDetails = res
    //   //   })
    //   //   .catch(err => {
    //   //     // TODO handle error
    //   //     console.log('err', err)
    //   //   })

    //   // this.requestClassPlansForSubGroup(this.$route.params.subGroupCk)
    // }
  },
  async mounted() {
    console.log(
      'mounted',
      this.groupCkFromRouteParams,
      this.subGroupCkFromRouteParams
    );

    await this.fetchSubgroupData(
      this.groupCkFromRouteParams,
      this.subGroupCkFromRouteParams
    );
    /*
          03/04/2019 -- EyeMed IT is not going to develop the endpoint to get benefits from a subgroup
          This is commented out for now but they may request this feature in the near future
          If so, in <template>, uncomment the block that shows the benefits and uncomment the API call directly below
          this comment...:
       */
    // new GroupApi({ simulateExample: 'Get Group Benefit' })
    //   .benefits({
    //     groupCk: this.$route.params.groupCk,
    //     subGroupCk: this.$route.params.subGroupCk
    //   })
    //   .then(res => {
    //     this.benefits = res.benefits
    //   })
    //   .catch(err => {
    //     //TODO handle error
    //     console.log('err', err)
    //   })
    // new GroupApi({ simulateExample: 'Sub Group Detail' })
    //   .subGroup({
    //     groupCk: this.groupCkFromRouteParams,
    //     subGroupCk: this.subGroupCkFromRouteParams
    //   })
    //   .then(res => {
    //     this.subGroupDetails = res
    //   })
    //   .catch(err => {
    //     // TODO handle error
    //     console.log('err', err)
    //   })
    //   this.requestClassPlansForSubGroup(this.subGroupCkFromRouteParams)
  }
};
</script>
